/**
 * The auth guard. Everything above the entrance renders inside it.
 *
 * A component rather than a router `beforeLoad`, deliberately: the session query lives in
 * the TanStack Query cache that `AppProviders` creates *above* `RouterProvider`, so a
 * loader would need the client threaded through router context to reach the same cache —
 * and two caches for one session is how a screen renders for a user who has been signed out.
 *
 * Its whole job is the three answers `GET /auth/me` can give: still loading, not you, you —
 * plus one more since `KLQ-20260819-007`: **which company this tab is acting as.**
 *
 * **"Not you" remembers where you were going** (`MCK-20260521-009`). The redirect is
 * `/login?next=<path and search>`, so the sign-in that follows returns to the deep link rather
 * than to `/`. `next.ts` is what keeps that parameter from being an open redirector.
 *
 * While it is still asking, it draws the page loader — but only past `useLongWait`'s delay.
 * Nothing is known yet at that point, not even a layout, which is the one situation
 * CLAUDE.md ("Loading states") reserves the section for; everything below this guard has a
 * shape to promise and draws a `Skeleton` instead.
 *
 * **On a cold start the delay is already spent.** `index.html` draws the same building section
 * as static markup from the first paint, and React's first commit wipes it (`lib/boot.ts`), so
 * waiting 200 ms here would blank the screen in the middle of a wait that never stopped. This
 * is the one caller that passes `initiallyLong`, and the one that passes `phaseMs` — the loop
 * continues from the frame the boot copy had reached rather than starting again.
 *
 * The active workspace is set here, during render, rather than in an effect, and the timing
 * is the reason.
 * The value has to be in place before any child fires its first request, and an effect runs
 * *after* children have rendered — which would send the first request of every screen with no
 * header and have it answered by the home workspace. The assignment is idempotent and to a
 * module variable, so a double render under StrictMode changes nothing.
 */

import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useRouter } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect, useState } from 'react';

import { setActiveWorkspace } from '@/api/client';
import { PageLoader } from '@/components';
import { bootShowedLoader } from '@/lib/boot';
import { useLocaleStore, useMessages } from '@/lib/i18n';
import { LONG_WAIT_MS, useLongWait } from '@/lib/useLongWait';

import { useActiveWorkspaceId } from './activeWorkspace';
import { isReturnPath } from './next';
import { SESSION_QUERY_KEY, meQueryOptions, useMe } from './queries';
import { studioLocaleOf } from './studioLanguage';

export interface RequireSessionProps {
  readonly children: ReactNode;
  /**
   * What an anonymous caller sees instead of the sign-in redirect.
   *
   * Exactly one route passes it: `/`, whose signed-out answer is the landing page
   * (`features/landing`, 2026-09-05). It is a prop rather than a second route because `/` is
   * one address with two audiences — a signed-in person still lands on B1, and a signed-out
   * bookmark of `/` renders the public page rather than bouncing through a redirect to reach
   * it. Everywhere else the fallback is absent and the guard still ends at `/login`, which is
   * the only correct answer for a protected screen.
   */
  readonly fallback?: ReactNode;
}

export function RequireSession({ children, fallback }: RequireSessionProps) {
  const t = useMessages('app');
  const queryClient = useQueryClient();
  const { data, error, isPending } = useMe();
  const failed = error !== null;
  // Where the caller was trying to go, for the sign-in to return to (`MCK-20260521-009`). Path
  // and search, never the origin: `next` only ever names somewhere inside this application.
  //
  // **Read, not subscribed** — `router.state`, never `useRouterState`. A subscription re-renders
  // this guard the moment `<Navigate>` below moves the location to `/login?next=…`, the guard is
  // still in its failed state, and the *new* address becomes the next `next`:
  // `/login?next=/login?next=/login?next=…` until the tab runs out of memory. Found 2026-09-14
  // by the deep-link journey in `e2e/landing.spec.ts`, which never reached `/login` at all.
  const router = useRouter();
  const here = `${router.state.location.pathname}${router.state.location.searchStr}`;

  // One state initialiser for one fact: the boot loader was on screen when this mounted, and
  // the clock read `phase` at that moment. In a state initialiser because `performance.now()`
  // has to be the first render's, not whichever render happens to ask; `undefined` on every
  // client-side navigation, where there was no boot loader to continue.
  const [phaseMs] = useState<number | undefined>(() =>
    bootShowedLoader() ? performance.now() : undefined,
  );
  const waitIsLong = useLongWait(isPending, LONG_WAIT_MS, { initiallyLong: phaseMs !== undefined });

  // See the module note: during render, deliberately, so the first request of every screen
  // below already names the company the URL asked for.
  const activeWorkspaceId = useActiveWorkspaceId();
  setActiveWorkspace(activeWorkspaceId);

  /**
   * The studio's language (`MCK-20260521-010`, owner 2026-09-12), adopted here because this is
   * where the session payload first exists and because everything that renders a message
   * renders below it.
   *
   * `useLayoutEffect` rather than `useEffect`: the store update re-renders every screen that
   * reads a message, and an effect that runs *after* paint would show one frame of the
   * reader's browser language before correcting itself — which is the flash the criterion's
   * "no page reload" is really about. A layout effect commits before the browser paints, so
   * the wrong language is never on screen.
   *
   * `null` when there is no session or the studio has not been claimed, which hands the
   * entrance screens back to the reader's own pre-auth choice.
   */
  const studioTag = studioLocaleOf(data, activeWorkspaceId);
  const adoptStudioLocale = useLocaleStore((state) => state.adoptStudioLocale);
  useLayoutEffect(() => {
    adoptStudioLocale(studioTag);
  }, [studioTag, adoptStudioLocale]);

  // A failed session must not survive the navigation away from this guard. A cached error
  // would make the next visit to a protected route bounce to the entrance without asking the
  // server again — including the visit right after a successful sign-in.
  //
  // On unmount, not on failure. Removing a query while its own observer still watches it
  // makes TanStack rebuild and refetch it on the next render; with a `fallback` keeping this
  // component mounted that was an endless pending → 401 → remove loop — the landing page and
  // "checking session" alternating every frame (2026-09-05). Before the fallback existed the
  // `<Navigate>` below unmounted the guard in the same commit, which is why it never showed.
  useEffect(
    () => () => {
      if (queryClient.getQueryState(meQueryOptions.queryKey)?.status === 'error') {
        queryClient.removeQueries({ queryKey: SESSION_QUERY_KEY });
      }
    },
    [queryClient],
  );

  if (isPending) {
    // A route with a public answer has a public audience; do not greet it with a sentence
    // about a session it never had.
    return waitIsLong ? (
      <PageLoader
        label={t(fallback === undefined ? 'session.checking' : 'session.loading')}
        phaseMs={phaseMs}
      />
    ) : null;
  }

  // Any failure lands the caller on the entrance — or on this route's own public answer,
  // where it has one — and not only on a 401: by this point the client's
  // own refresh-and-retry has already been spent (`api/client.ts`), and a session we cannot
  // read is a session we cannot honour. A blank protected screen would be worse.
  if (failed || data === undefined) {
    if (fallback !== undefined) return <>{fallback}</>;
    // `next` unless the address is `/`, which the landing rule already resolves — and unless it
    // is not a path `safeNext` would honour, so the entrance never shows a value it will refuse.
    return isReturnPath(here) ? (
      <Navigate to="/login" search={{ next: here }} replace />
    ) : (
      <Navigate to="/login" replace />
    );
  }

  return <>{children}</>;
}
